import * as React from 'react';
import { observer } from 'src/utils/mobx-react';
import { useAppStateContext } from 'src/appState/AppState';
import { Amount } from 'src_common/common/amount/Amount';
import {
    BetslipAllSinglesId,
    BetslipCombinationId,
    BetslipRabId,
    BetslipSingleId,
    BetslipMultibetId,
} from 'src/domains/sportsbook/betting/models/BetslipIdModels';
import {
    CurrencyStyle,
    StakeInputStyle,
    StakeInputWrapper,
} from 'src/domains/sportsbook/betting/ui/betSlip/stakeInputMobx/StakeInputMobx.style';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { AppSportsBookState } from 'src/domains/sportsbook/state/AppSportsBook.state';

interface StakeInputNewPropsType {
    selectionId: BetslipSingleId | BetslipCombinationId | BetslipRabId | BetslipAllSinglesId | BetslipMultibetId;
    stakePerLine?: Amount | null;
}

export const StakeInputMobx = observer('StakeInputMobx', (props: StakeInputNewPropsType) => {
    const appState = useAppStateContext();
    const common = useCommon();
    const configState = ConfigComponents.get(common).config;
    const { selectionId, stakePerLine } = props;
    const { isReferred } = AppSportsBookState.get(appState.common).betSlipState.referralState;
    const { onFocusSelection, isVisibleKeyBoard, focusedSelectionId } = AppSportsBookState.get(appState.common)
        .betSlipState.basicBetSlipState;
    const betslipData = AppSportsBookState.get(appState.common).betSlipState.betslipData;
    const moneySymbol = AppSportsBookState.get(appState.common).moneySymbol;
    const placeholder = configState.decimalFormatTextForPlaceholder;

    const { inputVariant, betslipVariant } = configState.componentVariants;

    if (isReferred) {
        return (
            <StakeInputWrapper
                variant={inputVariant}
                betslipVariant={betslipVariant}
            >
                <CurrencyStyle>{moneySymbol}</CurrencyStyle>
                <StakeInputStyle
                    data-test='stake-input'
                    type='tel'
                    value={stakePerLine?.value ?? undefined}
                    placeholder={placeholder}
                    disabled={true}
                />
            </StakeInputWrapper>
        );
    }

    const item = selectionId.getModel();
    if (item === null) {
        return null;
    }

    const { stakeInput } = item;
    if (stakeInput === null) {
        return null;
    }

    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        stakeInput.changeFromInput(e.currentTarget.value);
        AppSportsBookState.get(appState.common).betSlipState.betSlipSummaryState.clearError();
    };

    const handleScroll = (): void => {
        const { refElem, legsIds2 } = AppSportsBookState.get(appState.common).betSlipState;
        const refSelection = betslipData.getRef(selectionId);

        if (refSelection !== null && refElem !== null && legsIds2.length > 1) {
            setTimeout(() => {
                refElem.scrollTo({
                    top: refSelection.offsetTop - 52,
                    left: 0,
                    behavior: 'smooth',
                });
            }, 200);
        }
    };

    const onClick = (e: React.MouseEvent<HTMLInputElement>): void => {
        e.preventDefault();
        onFocusSelection(selectionId);
        handleScroll();
    };

    const onTouchEnd = (e: React.TouchEvent<HTMLInputElement>): void => {
        if (isVisibleKeyBoard) {
            e.preventDefault();
            onFocusSelection(selectionId);
        }

        handleScroll();
    };

    return (
        <StakeInputWrapper
            variant={inputVariant}
            betslipVariant={betslipVariant}
        >
            <CurrencyStyle>{moneySymbol}</CurrencyStyle>
            <StakeInputStyle
                data-test='stake-input'
                type='tel'
                value={stakeInput.valueForInput}
                onChange={handleOnChange}
                onClick={onClick}
                onTouchEnd={onTouchEnd}
                placeholder={placeholder}
                disabled={focusedSelectionId === selectionId && isVisibleKeyBoard ? true : isReferred}
            />
        </StakeInputWrapper>
    );
});
